import {
  BackComponent,
  Banner,
  FormInput,
  FormLocation,
  Loader,
  PageActionsStyles,
  PageMainContent,
  Typography,
} from '@/components'
import { ComponentColors } from '@/enums'
import { FormProvider, useSnackbar } from '@/providers'
import {
  useLocationHome,
  useRequestEditLocation,
  useUpdateAddress,
  useUpdateLocation,
} from '@/services'
import { useGoBack } from '@/utils'
import { Button } from '@surein/ui'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  checkCompletedAssessment,
  checkPoaSigned,
  makeInitialValues,
  transform,
} from './helpers'
import { PageHeader } from './styles'
import { validationSchema } from './validationSchema'

interface FormValues {
  name: string
  address: {
    addressId?: number
    city: string
    country: string
    postalCode: string
    street: string
    streetNr: string
  }
}

const PageForm = ({ data }: { data: any }) => {
  const { t } = useTranslation()
  const snackbar = useSnackbar()
  const { locationId = '' } = useParams()
  const updateLocationMutation = useUpdateLocation(locationId)
  const updateAddressMutation = useUpdateAddress(data.address.addressId)
  const requestEditLocationMutation = useRequestEditLocation(locationId)

  const handleBack = useGoBack()

  const disabled = checkCompletedAssessment(data) && checkPoaSigned(data)

  const editService = ({ name, address }: FormValues) =>
    disabled
      ? requestEditLocationMutation.mutateAsync()
      : Promise.all([
          updateLocationMutation.mutateAsync({
            name,
          }),
          updateAddressMutation.mutate(address),
        ])

  const handleSubmit = (values: FormValues) =>
    editService(values)
      .then(() => {
        handleBack()
        snackbar.success(t('snackbars.success.editLocation'))
      })
      .catch(() => snackbar.error(t('snackbars.error.generalMsg')))
  return (
    <>
      <BackComponent />
      <PageHeader>
        <Typography variant="h4">{t('locationPage.editLocation')}</Typography>
        {disabled && (
          <Banner
            color={ComponentColors.warning}
            text={t('snackbars.success.editRequestDisabled')}
          />
        )}
      </PageHeader>
      <FormProvider
        initialValues={makeInitialValues(data)}
        onSubmit={handleSubmit}
        validationSchema={
          disabled
            ? null
            : validationSchema(t('validations', { returnObjects: true }))
        }
      >
        <FormInput
          // Location name is not editable in the UI, available only in the admin app
          disabled
          label={t('locationPage.locationName')}
          name="name"
        />
        <FormLocation disabled={disabled} name="address" />
        <PageActionsStyles>
          <Button onClick={handleBack} variant="muted" shape="square">
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" shape="square">
            {disabled ? t('common.requestEdit') : t('common.save')}
          </Button>
        </PageActionsStyles>
      </FormProvider>
    </>
  )
}

export const EditLocationPage = () => {
  const { locationId = '' } = useParams()
  const { data, isLoading } = useLocationHome(
    { id: locationId },
    {
      // @ts-expect-error - fixed with another PR
      select: transform,
    },
  )

  if (isLoading) return <Loader />

  return (
    <>
      <PageMainContent size="sm">
        <PageForm data={data} />
      </PageMainContent>
    </>
  )
}
